/**
 * P6 cinematic kill-lighting — pure interpolation math.
 *
 * On a kill rising edge, the existing magica-voxel-preset directional light
 * pulses brighter + warmer for `KILL_LIGHT_PULSE_DURATION_MS`, then
 * decays back to baseline. We don't add a new light source — we just
 * temporarily nudge the one that's already there.
 *
 * This module exports the interpolation curve so it unit-tests as
 * plain TS; the component in `Lights.tsx` calls it from useFrame.
 */

export const KILL_LIGHT_PULSE_DURATION_MS = 900;
export const KILL_LIGHT_PULSE_PEAK_MULTIPLIER = 1.5;
/** Hex color the light warms toward at the peak of the pulse. */
export const KILL_LIGHT_WARM_COLOR = "#ffb060";
/** Ambient light dips this fraction during the pulse so the directional
 *  highlight reads stronger. */
export const KILL_LIGHT_AMBIENT_DIP = 0.2;

export interface KillPulseState {
  /** Multiplier on the directional light's baseline intensity. */
  intensityMul: number;
  /** Blend factor toward `KILL_LIGHT_WARM_COLOR` (0..1). */
  warmBlend: number;
  /** Multiplier on ambient/fill intensities (1 = baseline, <1 = dipped). */
  ambientMul: number;
  /** Whether the pulse is still active. False ⇒ the scene should release
   *  the light back to baseline and ignore further state from this call. */
  active: boolean;
}

const REST: KillPulseState = {
  intensityMul: 1,
  warmBlend: 0,
  ambientMul: 1,
  active: false,
};

/**
 * Compute the kill-pulse state at `nowMs` given the rising-edge
 * timestamp `startedAt`. Returns the rest state when the pulse is
 * inactive or has expired.
 *
 * Curve: triangle wave — ramp 0→peak over the first half, peak→0 over
 * the second half. Symmetric so the visual impulse reads as a single
 * "thump" timed against the 900ms mech-fade.
 */
export function killPulseState(startedAt: number | null, nowMs: number): KillPulseState {
  if (startedAt === null) return REST;
  const elapsed = nowMs - startedAt;
  if (elapsed < 0 || elapsed >= KILL_LIGHT_PULSE_DURATION_MS) return REST;
  const t = elapsed / KILL_LIGHT_PULSE_DURATION_MS;
  // Triangle: 0..0.5 ramps up, 0.5..1 ramps down.
  const tri = t < 0.5 ? t * 2 : (1 - t) * 2;
  const intensityMul = 1 + (KILL_LIGHT_PULSE_PEAK_MULTIPLIER - 1) * tri;
  const warmBlend = tri;
  const ambientMul = 1 - KILL_LIGHT_AMBIENT_DIP * tri;
  return { intensityMul, warmBlend, ambientMul, active: true };
}
